import React, { useEffect, useRef, useState } from "react";

const defaultItems = Array.from({ length: 16 }, (_, index) => ({
  id: index + 1,
  serialNo: index + 1,
  question: `Question ${index + 1}`,
  status:
    index % 5 === 0
      ? "correct"
      : index % 4 === 0
      ? "incorrect"
      : index % 3 === 0
      ? "solved"
      : "unsolved",
}));

function stripHtml(value) {
  return String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function QuestionPallete({
  items = defaultItems,
  activeQuestionId = 1,
  activeQuestionNumber = 0,
  onSelect = () => {},
  canShowMore = false,
  onShowMore = () => {},
  summaryCounts = null,
  activeTab: controlledActiveTab,
  onTabChange,
  isLoadingMore = false,
  showCorrectness = true,
}) {
  const [internalActiveTab, setInternalActiveTab] = useState("grid");
  const activeTab = controlledActiveTab || internalActiveTab;
  const gridListRef = useRef(null);
  const textListRef = useRef(null);
  const fallbackSolvedCount = items.filter(
    (item) => item.status === "solved" || item.status === "correct" || item.status === "incorrect"
  ).length;
  const fallbackCorrectCount = items.filter((item) => item.status === "correct").length;
  const fallbackIncorrectCount = items.filter((item) => item.status === "incorrect").length;
  const fallbackUnsolvedCount = Math.max(0, items.length - fallbackSolvedCount);
  const solvedCount = Number(summaryCounts?.solvedCount ?? fallbackSolvedCount);
  const unsolvedCount = Number(summaryCounts?.unsolvedCount ?? fallbackUnsolvedCount);
  const correctCount = Number(summaryCounts?.correctCount ?? fallbackCorrectCount);
  const incorrectCount = Number(summaryCounts?.incorrectCount ?? fallbackIncorrectCount);
  const resolveDisplayStatus = (status) => {
    if (!showCorrectness && (status === "correct" || status === "incorrect")) {
      return "solved";
    }

    return status;
  };
  const isItemActive = (item) =>
    Number(activeQuestionId) === Number(item.id) ||
    Number(activeQuestionNumber) ===
      Number(item.serialNo || item.serial || item.question_index || item.question_number || 0);

  const handleTabChange = (nextTab) => {
    if (!controlledActiveTab) {
      setInternalActiveTab(nextTab);
    }
    onTabChange?.(nextTab);
  };

  useEffect(() => {
    const container = activeTab === "grid" ? gridListRef.current : textListRef.current;
    if (!container) {
      return;
    }
    const activeNode = container.querySelector('[data-active="true"]');
    if (activeNode && typeof activeNode.scrollIntoView === "function") {
      activeNode.scrollIntoView({ block: "center", inline: "nearest" });
    }
  }, [activeQuestionId, activeTab, items]);

  return (
    <div className="palette-container">
      <div className="qbank-paletteTabs">
        <button
          type="button"
          className={`qbank-paletteTab${activeTab === "grid" ? " is-active" : ""}`}
          onClick={() => handleTabChange("grid")}
        >
          <i className="bi bi-grid-3x3-gap" /> Grid View
        </button>
        <button
          type="button"
          className={`qbank-paletteTab${activeTab === "list" ? " is-active" : ""}`}
          onClick={() => handleTabChange("list")}
        >
          <i className="bi bi-list-task" /> List View
        </button>
      </div>

      <div className="palette-summary">
        <div className="summary-card solved">
          <div className="count green">{solvedCount}</div>
          <div><h6>Solved</h6></div>
        </div>
        <div className="summary-card unsolved">
          <div className="count gray">{unsolvedCount}</div>
          <div><h6>Unsolved</h6></div>
        </div>
        {showCorrectness ? (
          <>
            <div className="summary-card correct">
              <div className="count mint">{correctCount}</div>
              <div><h6>Correct</h6></div>
            </div>
            <div className="summary-card incorrect">
              <div className="count pink">{incorrectCount}</div>
              <div><h6>Incorrect</h6></div>
            </div>
          </>
        ) : null}
      </div>

      {activeTab === "grid" ? (
        <div className="question-grid" ref={gridListRef}>
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              data-active={isItemActive(item) ? "true" : "false"}
              className={`question-btn ${resolveDisplayStatus(item.status)} ${
                isItemActive(item) ? "active" : ""
              }`}
              onClick={() => onSelect(item)}
            >
              {item.serialNo || item.serial || item.question_index || item.id}
            </button>
          ))}
        </div>
      ) : (
        <div className="qbank-paletteList" ref={textListRef}>
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              data-active={isItemActive(item) ? "true" : "false"}
              className={`qbank-paletteList__item ${resolveDisplayStatus(item.status)} ${
                isItemActive(item) ? "active" : ""
              }`}
              onClick={() => onSelect(item)}
            >
              <span className="qbank-paletteList__number">
                {item.serialNo || item.serial || item.question_index || item.id}
              </span>
              <span className="qbank-paletteList__text">
                {stripHtml(item.question || item.questionHtml || `Question ${index + 1}`)}
              </span>
            </button>
          ))}
        </div>
      )}

      {canShowMore ? (
        <button type="button" className="downl-btn question-more-btn" aria-label="Show more questions" onClick={onShowMore}>
          <i className={`bi ${isLoadingMore ? "bi-arrow-repeat" : "bi-chevron-down"}`} />
        </button>
      ) : null}

      <div className="bottom-btn">
        <button type="button" className="questions-btn">
          Questions
        </button>
      </div>
    </div>
  );
}

export default QuestionPallete;
